/**
 * File-based Database for Analysis Data
 * Mimics MongoDB structure with collections and documents
 * Uses localStorage for persistence (browser can't write to files directly)
 * Provides export/import functionality for file-based storage
 */

// Database structure similar to MongoDB
// {
//   analysis: [
//     {
//       _id: "unique-id",
//       assetId: "asset-id",
//       issueKey: "highConsumptionVariance",
//       issueId: "ASSETCODE-ISSUE-DATE",
//       analysis: "...",
//       suggestedAction: "...",
//       finalAction: "...",
//       alertStatus: "confirmed" | "discarded" | null,
//       ... other fields
//     }
//   ]
// }

const DB_NAME = 'iot_analysis_db';
const DB_VERSION = '1.0';

/**
 * Initialize the database
 */
export const initDB = () => {
  try {
    const existing = localStorage.getItem(DB_NAME);
    if (!existing) {
      const initialDB = {
        _version: DB_VERSION,
        _createdAt: new Date().toISOString(),
        _updatedAt: new Date().toISOString(),
        analysis: []
      };
      localStorage.setItem(DB_NAME, JSON.stringify(initialDB));
      return initialDB;
    }
    return JSON.parse(existing);
  } catch (e) {
    console.error('Error initializing database:', e);
    return {
      _version: DB_VERSION,
      _createdAt: new Date().toISOString(),
      _updatedAt: new Date().toISOString(),
      analysis: []
    };
  }
};

/**
 * Get all documents from analysis collection
 */
export const getAllAnalysis = () => {
  try {
    const db = initDB();
    return db.analysis || [];
  } catch (e) {
    console.error('Error getting all analysis:', e);
    return [];
  }
};

/**
 * Get analysis data for a specific asset
 */
export const getAnalysisByAssetId = (assetId) => {
  try {
    const allAnalysis = getAllAnalysis();
    const assetAnalysis = {};
    
    allAnalysis.forEach(doc => {
      if (doc.assetId === assetId) {
        assetAnalysis[doc.issueKey] = doc;
      }
    });
    
    return assetAnalysis;
  } catch (e) {
    console.error('Error getting analysis by asset ID:', e);
    return {};
  }
};

/**
 * Get a single analysis document by assetId and issueKey
 */
export const getAnalysisDocument = (assetId, issueKey) => {
  try {
    const allAnalysis = getAllAnalysis();
    return allAnalysis.find(
      doc => doc.assetId === assetId && doc.issueKey === issueKey
    ) || null;
  } catch (e) {
    console.error('Error getting analysis document:', e);
    return null;
  }
};

/**
 * Save or update an analysis document
 */
export const saveAnalysisDocument = (assetId, issueKey, data) => {
  try {
    const db = initDB();
    const allAnalysis = db.analysis || [];
    
    // Find existing document
    const existingIndex = allAnalysis.findIndex(
      doc => doc.assetId === assetId && doc.issueKey === issueKey
    );
    
    const document = {
      _id: existingIndex >= 0 ? allAnalysis[existingIndex]._id : generateId(),
      assetId: assetId,
      issueKey: issueKey,
      ...data,
      updatedAt: new Date().toISOString(),
      _updatedAt: new Date().toISOString()
    };
    
    if (existingIndex >= 0) {
      // Update existing
      allAnalysis[existingIndex] = document;
    } else {
      // Insert new
      document._createdAt = new Date().toISOString();
      allAnalysis.push(document);
    }
    
    db.analysis = allAnalysis;
    db._updatedAt = new Date().toISOString();
    
    localStorage.setItem(DB_NAME, JSON.stringify(db));
    return document;
  } catch (e) {
    console.error('Error saving analysis document:', e);
    throw e;
  }
};

/**
 * Save multiple analysis documents for an asset
 */
export const saveAssetAnalysis = (assetId, analysisData) => {
  try {
    const issueKeys = Object.keys(analysisData);
    const saved = {};
    
    issueKeys.forEach(issueKey => {
      const document = saveAnalysisDocument(assetId, issueKey, analysisData[issueKey]);
      saved[issueKey] = document;
    });
    
    return saved;
  } catch (e) {
    console.error('Error saving asset analysis:', e);
    throw e;
  }
};

/**
 * Delete an analysis document
 */
export const deleteAnalysisDocument = (assetId, issueKey) => {
  try {
    const db = initDB();
    const allAnalysis = db.analysis || [];
    
    db.analysis = allAnalysis.filter(
      doc => !(doc.assetId === assetId && doc.issueKey === issueKey)
    );
    
    db._updatedAt = new Date().toISOString();
    localStorage.setItem(DB_NAME, JSON.stringify(db));
    return true;
  } catch (e) {
    console.error('Error deleting analysis document:', e);
    return false;
  }
};

/**
 * Get all analysis data formatted for the component (legacy format)
 */
export const getAnalysisDataForComponent = () => {
  try {
    const allAnalysis = getAllAnalysis();
    const formatted = {};
    
    allAnalysis.forEach(doc => {
      if (!formatted[doc.assetId]) {
        formatted[doc.assetId] = {};
      }
      
      // Convert document back to component format (without _id, _createdAt, etc.)
      const { _id, _createdAt, _updatedAt, assetId, issueKey, ...componentData } = doc;
      formatted[doc.assetId][doc.issueKey] = componentData;
    });
    
    return formatted;
  } catch (e) {
    console.error('Error formatting analysis data:', e);
    return {};
  }
};

/**
 * Export database to JSON file (download)
 */
export const exportDatabase = () => {
  try {
    const db = initDB();
    const dataStr = JSON.stringify(db, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `iot-analysis-db-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    return true;
  } catch (e) {
    console.error('Error exporting database:', e);
    return false;
  }
};

/**
 * Import database from JSON file
 */
export const importDatabase = (file) => {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const imported = JSON.parse(e.target.result);
          
          // Validate structure
          if (!imported.analysis || !Array.isArray(imported.analysis)) {
            reject(new Error('Invalid database format'));
            return;
          }
          
          // Merge with existing data (or replace)
          const db = initDB();
          const existingIds = new Set(db.analysis.map(doc => doc._id));
          
          imported.analysis.forEach(doc => {
            const existingIndex = db.analysis.findIndex(d => d._id === doc._id);
            if (existingIndex >= 0) {
              // Update existing
              db.analysis[existingIndex] = {
                ...db.analysis[existingIndex],
                ...doc,
                _updatedAt: new Date().toISOString()
              };
            } else {
              // Add new
              db.analysis.push({
                ...doc,
                _createdAt: doc._createdAt || new Date().toISOString(),
                _updatedAt: new Date().toISOString()
              });
            }
          });
          
          db._updatedAt = new Date().toISOString();
          localStorage.setItem(DB_NAME, JSON.stringify(db));
          resolve(db);
        } catch (parseError) {
          reject(parseError);
        }
      };
      reader.onerror = () => reject(new Error('Error reading file'));
      reader.readAsText(file);
    } catch (e) {
      reject(e);
    }
  });
};

/**
 * Clear all database data
 */
export const clearDatabase = () => {
  try {
    localStorage.removeItem(DB_NAME);
    initDB();
    return true;
  } catch (e) {
    console.error('Error clearing database:', e);
    return false;
  }
};

/**
 * Get database statistics
 */
export const getDatabaseStats = () => {
  try {
    const db = initDB();
    const allAnalysis = db.analysis || [];
    
    const stats = {
      totalDocuments: allAnalysis.length,
      totalAssets: new Set(allAnalysis.map(doc => doc.assetId)).size,
      byStatus: {
        confirmed: allAnalysis.filter(doc => doc.alertStatus === 'confirmed').length,
        discarded: allAnalysis.filter(doc => doc.alertStatus === 'discarded').length,
        noStatus: allAnalysis.filter(doc => 
          doc.finalAction && !doc.alertStatus
        ).length
      },
      byAnalysisStatus: {
        notAnalysed: 0,
        underAnalysis: 0,
        analysed: 0,
        actionTaken: 0
      },
      lastUpdated: db._updatedAt,
      createdAt: db._createdAt,
      version: db._version
    };
    
    allAnalysis.forEach(doc => {
      if (!doc.analysis) {
        stats.byAnalysisStatus.notAnalysed++;
      } else if (doc.analysis && !doc.suggestedAction) {
        stats.byAnalysisStatus.underAnalysis++;
      } else if (doc.analysis && doc.suggestedAction && !doc.finalAction) {
        stats.byAnalysisStatus.analysed++;
      } else if (doc.finalAction) {
        stats.byAnalysisStatus.actionTaken++;
      }
    });
    
    return stats;
  } catch (e) {
    console.error('Error getting database stats:', e);
    return null;
  }
};

/**
 * Generate unique ID for documents
 */
const generateId = () => {
  return `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

